import { Card, KpiCard, Notice, Tag, AsyncBoundary } from '@kollez/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import { useAuthStore } from '@/lib/authStore';
import { useMeDashboard, useMyBadges, type MyBadge } from '@/lib/queries';

interface StudentProfileDoc {
  _id: string;
  academic?: {
    program?: { name?: string } | string;
    specialization?: { name?: string } | string;
    college?: { name?: string } | string;
    batchYear?: number;
    cgpa?: number;
    /** Server returns either a plain number (legacy demo seeds) or `{current, history}` (real schema). */
    backlogs?: number | { current?: number; history?: unknown[] };
    class10Percentage?: number;
    class12Percentage?: number;
    rollNumber?: string;
  };
  skills?: Array<{ name: string; level?: string; verified?: boolean }>;
  projects?: Array<{ title: string; url?: string }>;
  provenance?: { source?: string; selfDeclaredCollege?: string | { name?: string } };
  // Schema truth (models/StudentProfile.ts): isPlaced / highestCTC / sitOut — no
  // `status` or `ctc` fields exist on the sub-doc.
  placementStatus?: { isPlaced?: boolean; highestCTC?: number; sitOut?: boolean };
}

interface Profile360 {
  overall?: number;
  dimensions?: Array<{ key: string; score?: number }>;
}

function rel<T>(v: T | { name?: string } | null | undefined): string {
  if (!v) return '—';
  if (typeof v === 'string') return v;
  return (v as { name?: string }).name ?? '—';
}

export function StudentProfilePage() {
  const user = useAuthStore((s) => s.user);
  const profileQ = useQuery({
    queryKey: ['student', 'me', 'profile'],
    queryFn: () => apiGet<{ profile: StudentProfileDoc }>('/students/me').catch(() => ({ profile: {} as StudentProfileDoc })),
  });
  const readinessQ = useQuery({
    queryKey: ['student', 'me', 'profile-360'],
    queryFn: () => apiGet<Profile360>('/students/me/profile-360').catch(() => ({} as Profile360)),
  });
  // Composite dashboard already carries applicationsByStatus + offersByStatus, so we
  // avoid the two extra list fetches for a page that only needs counts.
  const dash = useMeDashboard();

  const p = profileQ.data?.profile ?? ({} as StudentProfileDoc);
  const academic = p.academic ?? {};
  const skills = p.skills ?? [];
  const projects = p.projects ?? [];
  const readiness = readinessQ.data?.overall;
  // Backlogs comes back either as a number or a { current, history } sub-object — normalize.
  const backlogsCount = typeof academic.backlogs === 'number'
    ? academic.backlogs
    : (academic.backlogs?.current ?? 0);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">My profile</p>
          <h1 className="h-page">{user?.fullName ?? '—'}</h1>
          <p className="subhead">
            {rel(academic.program)} · {rel(academic.specialization)} · {rel(academic.college)}
            {academic.batchYear ? ` · Batch ${academic.batchYear}` : ''}
          </p>
        </div>
      </div>

      <AsyncBoundary kind="detail" loading={profileQ.isLoading} error={profileQ.error}>
        <div className="grid gap-3 md:grid-cols-4">
          <KpiCard label="CGPA" value={academic.cgpa != null ? String(academic.cgpa) : '—'} />
          <KpiCard
            label="Backlogs"
            value={backlogsCount}
            tone={backlogsCount === 0 ? 'success' : 'warning'}
          />
          <KpiCard label="Skills" value={skills.length} />
          <KpiCard
            label="Profile 360"
            value={readiness != null ? `${Math.round(readiness)}%` : '—'}
            tone={readiness != null && readiness >= 70 ? 'success' : 'warning'}
          />
        </div>

        {p.placementStatus?.isPlaced && (
          <Notice tone="success">
            <b>Placed.</b>{typeof p.placementStatus.highestCTC === 'number' && p.placementStatus.highestCTC > 0
              ? ` Highest CTC ₹${p.placementStatus.highestCTC.toLocaleString('en-IN')}.`
              : ''}
          </Notice>
        )}
        {p.placementStatus?.sitOut && (
          <Notice tone="warning">
            <b>Sit-out period.</b> Your placement policy pauses new applications after your first accepted offer.
          </Notice>
        )}

        {p.provenance?.source === 'direct_signup' && (
          <Notice tone="warning">
            <b>Self-declared:</b> {rel(p.provenance.selfDeclaredCollege)}. Waiting for your placement office to verify.
          </Notice>
        )}

        <BadgeStripCard />

        <div className="grid gap-4 md:grid-cols-2">
          <Card padding="lg">
            <h2 className="h-section mb-3">Skills</h2>
            {skills.length === 0 ? (
              <div className="cell-sub">
                No skills recorded yet.{' '}
                <Link className="link" to="/skills">Add skills</Link>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {skills.map((s) => <Tag key={s.name} pressed>{s.name}</Tag>)}
              </div>
            )}
          </Card>

          <Card padding="lg">
            <h2 className="h-section mb-3">Projects</h2>
            {projects.length === 0 ? (
              <div className="cell-sub">
                No projects yet.{' '}
                <Link className="link" to="/projects">Add a project</Link>
              </div>
            ) : (
              <ul className="grid gap-2 text-sm">
                {projects.map((p) => (
                  <li key={p.title}>
                    <b>{p.title}</b>{p.url ? <> · <a className="link" href={p.url}>Link</a></> : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card padding="lg">
            <div className="cell-sub">Applications</div>
            <div className="h-serif" style={{ fontSize: '1.6rem' }}>
              {Object.values(dash.data?.kpis.applicationsByStatus ?? {}).reduce((n, v) => n + v, 0)}
            </div>
            <Link className="link text-sm" to="/applications">See all</Link>
          </Card>
          <Card padding="lg">
            <div className="cell-sub">Offers</div>
            <div className="h-serif" style={{ fontSize: '1.6rem' }}>
              {Object.values(dash.data?.kpis.offersByStatus ?? {}).reduce((n, v) => n + v, 0)}
            </div>
            <Link className="link text-sm" to="/offers">See offers</Link>
          </Card>
          <Card padding="lg">
            <div className="cell-sub">Roll no.</div>
            <div className="h-serif" style={{ fontSize: '1.6rem' }}>{academic.rollNumber ?? '—'}</div>
          </Card>
        </div>
      </AsyncBoundary>
    </div>
  );
}

/**
 * v1.6 §7.9 badge strip. Shows the top 6 badges the student has earned. Sorted server-side
 * by levelRank desc then issuedAt desc so the most impressive credentials lead. Tier chip
 * signals trust: tier_1 = Kollez, tier_2 = trusted partner, tier_3 = standard partner.
 */
function BadgeStripCard() {
  const q = useMyBadges();
  return (
    <Card padding="lg">
      <div className="flex items-center justify-between mb-3">
        <h2 className="h-section">Portable badges</h2>
        <Link className="link" to="/profile-360">See details</Link>
      </div>
      <AsyncBoundary
        kind="cards"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && (q.data?.rows.length ?? 0) === 0}
        emptyTitle="No badges yet"
        emptyHint="Clear a graded workflow stage and it lands here. Badges travel with you across drives."
      >
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {(q.data?.rows ?? []).slice(0, 6).map((b) => (
            <BadgeCell key={b._id} badge={b} />
          ))}
        </div>
      </AsyncBoundary>
    </Card>
  );
}

function BadgeCell({ badge }: { badge: MyBadge }) {
  const tierLabel = badge.issuerTier.replace('tier_', 'Tier ');
  // Step 49: whole cell is now a link into the badge detail page. Student can inspect any
  // credential they hold. The detail page (see BadgeDetail.tsx) covers status/provenance.
  return (
    <Link
      to={`/profile/badges/${badge._id}`}
      className="cell block"
      style={{ padding: '10px 12px', textDecoration: 'none', color: 'inherit' }}
    >
      <div className="flex items-center gap-2 mb-1">
        <b className="capitalize">{badge.skill.replace(/-/g, ' ')}</b>
        <Tag pressed>{badge.level}</Tag>
      </div>
      <div className="cell-sub flex flex-wrap items-center gap-1">
        <span>{badge.issuer?.name ?? 'Unknown issuer'}</span>
        <span>·</span>
        <span>{tierLabel}</span>
        {badge.certified ? <><span>·</span><span>Certified</span></> : null}
      </div>
    </Link>
  );
}
